import { useQuery } from '@tanstack/react-query';
import type { ActivityItem } from '@raro/domain';
import { EmptyState, Label, Notice, Segmented, Select, Tag, Title, cx } from '@raro/ui';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../../lib/api';

interface ActivityDay { day: string; today: string; previousDay: string | null; nextDay: string | null; items: ActivityItem[]; costUsd: number }
type Show = 'all' | 'people' | 'client' | 'machine' | 'errors';

const time = (iso: string) => new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
const dayLabel = (d: string, today: string) => {
  const text = new Date(`${d}T12:00:00-03:00`).toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'America/Sao_Paulo' });
  return d === today ? `hoje, ${text}` : text;
};
const usd = (v: number) => (v < 0.005 ? '< US$ 0,01' : `US$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/**
 * Atividade: o que as pessoas e a Machine fizeram, um dia por vez. É um registro para o sócio acompanhar a operação,
 * não um painel: sem indicador nem gráfico. Voz de pessoa em serifa itálica; a Machine com o rótulo de acento.
 */
export function AtividadePage() {
  const [params, setParams] = useSearchParams();
  const day = params.get('dia') ?? '';
  const q = useQuery({ queryKey: ['activity', day], queryFn: () => api.get<ActivityDay>(`/activity${day ? `?day=${day}` : ''}`), refetchInterval: day ? false : 60_000 });
  const [show, setShow] = useState<Show>('all');
  const [org, setOrg] = useState('');
  const [person, setPerson] = useState('');
  const d = q.data;
  const go = (to: string | null) => { if (to) setParams(to === d?.today ? {} : { dia: to }); };
  const items = d?.items ?? [];
  const orgs = [...new Map(items.filter((i) => i.organization).map((i) => [i.organization!.id, i.organization!.name])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const people = [...new Map(items.filter((i) => i.actor).map((i) => [i.actor!.id, i.actor!.name])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const shown = items.filter((i) =>
    (show === 'all' || (show === 'people' && i.who === 'person') || (show === 'client' && (i.who === 'client' || i.who === 'outside')) || (show === 'machine' && i.who === 'machine') || (show === 'errors' && Boolean(i.error)))
    && (!org || i.organization?.id === org) && (!person || i.actor?.id === person));
  const errors = items.filter((i) => i.error).length;
  return (
    <div className="rr-page rr-stack rr-stack--6">
      <header className="rr-stack rr-stack--2">
        <Label>Atividade</Label>
        <Title>o que aconteceu na machine.</Title>
        <p className="rr-muted">o que cada pessoa fez, da Raro e do cliente, e o que a Machine fez por conta própria. um dia por vez, do mais recente para o mais antigo.</p>
      </header>

      <div className="rr-row rr-row--between rr-row--wrap" style={{ gap: 12 }}>
        <div className="rr-row" style={{ gap: 12, alignItems: 'baseline' }}>
          <button type="button" className="rr-budget__toggle" disabled={!d?.previousDay} onClick={() => go(d?.previousDay ?? null)}>← dia anterior</button>
          <span className="rr-activity__day">{d ? dayLabel(d.day, d.today) : '…'}</span>
          {d?.nextDay && <button type="button" className="rr-budget__toggle" onClick={() => go(d.nextDay)}>dia seguinte →</button>}
          {d && d.day !== d.today && <button type="button" className="rr-budget__toggle" onClick={() => go(d.today)}>hoje</button>}
        </div>
        {d && <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>{items.length} registro(s){d.costUsd > 0 ? ` · IA do dia ${usd(d.costUsd)}` : ''}{errors ? ` · ${errors} com erro` : ''}</span>}
      </div>

      <div className="rr-row rr-row--wrap" style={{ gap: 12, alignItems: 'center' }}>
        <Segmented value={show} onChange={setShow} options={[{ value: 'all', label: 'tudo' }, { value: 'people', label: 'equipe Raro' }, { value: 'client', label: 'cliente' }, { value: 'machine', label: 'Machine' }, { value: 'errors', label: `erros${errors ? ` (${errors})` : ''}` }]} />
        {orgs.length > 1 && <Select value={org} onChange={(e) => setOrg(e.target.value)} aria-label="cliente" style={{ width: 'auto' }}><option value="">todos os clientes</option>{orgs.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</Select>}
        {people.length > 1 && <Select value={person} onChange={(e) => setPerson(e.target.value)} aria-label="pessoa" style={{ width: 'auto' }}><option value="">todas as pessoas</option>{people.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</Select>}
      </div>

      {q.isError && <Notice tone="error">{(q.error as Error).message}</Notice>}
      {q.isLoading && <p className="rr-faint">abrindo…</p>}
      {d && shown.length === 0 && <EmptyState title="nada neste recorte.">{items.length === 0 ? 'ninguém fez nada na Machine neste dia.' : 'há registros no dia, mas nenhum com este filtro.'}</EmptyState>}

      <div className="rr-activity">
        {shown.map((i) => (
          <div key={i.id} className={cx('rr-activity__row', i.error && 'rr-activity__row--error')}>
            <span className="rr-activity__time">{time(i.at)}</span>
            <span className="rr-activity__who">
              {i.who === 'machine' ? <span className="rr-label rr-label--accent">Machine</span> : i.who === 'outside' ? <span className="rr-faint">de fora</span> : <span className="rr-activity__name">{i.actor?.name}</span>}
              {i.who === 'client' && <Tag>cliente</Tag>}
            </span>
            <span className="rr-activity__what">
              {i.href ? <Link to={i.href} className="rr-activity__link">{i.text}</Link> : i.text}
              {i.detail && <span className="rr-faint"> · {i.detail}</span>}
              {i.error && <span className="rr-activity__error">{i.error}</span>}
            </span>
            <span className="rr-activity__org">{i.organization ? <Link to={`/clientes/${i.organization.id}`} className="rr-faint">{i.organization.name}</Link> : null}</span>
            <span className="rr-activity__cost rr-faint">{i.costUsd ? usd(i.costUsd) : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
